import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { MutationConfig, queryClient, TypedMutationConfig } from '@/lib/react-query';
import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
// import { TChannel } from '@/features/channel-integration/_shared/types/channel.type';
import { TGetListIntegrationMetaResponse } from './get-list-integration-meta.api';
import { GET_LIST_INTEGRATION_META_QUERY_KEY } from './get-list-integration-meta.api';
import { TChannel } from '../../../_shared/types/channel.type';

/**
 * Delete Integration Meta Input Schema
 */
export const deleteMetaIntegrationValidationSchema = z.object({
  organizationChannelId: z.string({ required_error: 'Organization channel id is required' }),
});
export type TDeleteMetaIntegrationValidation = z.infer<typeof deleteMetaIntegrationValidationSchema>;
export type TDeleteMetaIntegrationPayload = TDeleteMetaIntegrationValidation;
export type TDeleteMetaIntegrationResponse = ApiResponse<TChannel>;

export const deleteMetaIntegration = async (
  payload: TDeleteMetaIntegrationPayload
): Promise<TDeleteMetaIntegrationResponse> => {
  const url = `/integration/meta/org-channel/${payload.organizationChannelId}`;

  return axiosInstance.delete(url);
};

/**
 * Use Delete Integration Meta React-Query Hook
 */
type UseDeleteMetaIntegrationOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteMetaIntegration, AxiosErrorCustom>;
};

export const useDeleteMetaIntegration = ({ mutationConfig }: UseDeleteMetaIntegrationOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteMetaIntegration,
    ...restConfig,
  });
};

/**
 * Use Delete Integration Meta Optimistically React-Query Hook
 */
type UseDeleteMetaIntegrationOptimisticallyOptions = {
  mutationConfig?: MutationConfig<typeof deleteMetaIntegration>;
};

type TDeleteIntegrationMetaOptimisticallyContext = {
  previousData: TGetListIntegrationMetaResponse | undefined;
};

export const useDeleteMetaIntegrationOptimistically = ({
  mutationConfig,
}: UseDeleteMetaIntegrationOptimisticallyOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteMetaIntegration,
    onMutate: async (data: TDeleteMetaIntegrationValidation) => {
      await queryClient.cancelQueries({ queryKey: [GET_LIST_INTEGRATION_META_QUERY_KEY] });

      const previousData = queryClient.getQueryData<TGetListIntegrationMetaResponse>([
        GET_LIST_INTEGRATION_META_QUERY_KEY,
      ]);

      queryClient.setQueriesData<TGetListIntegrationMetaResponse>(
        { queryKey: [GET_LIST_INTEGRATION_META_QUERY_KEY] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            payload: {
              ...oldData.payload,
              rows: oldData.payload.rows.filter(
                (integration) => integration.organizationChannelId !== data.organizationChannelId
              ),
            },
          };
        }
      );

      return { previousData } as TDeleteIntegrationMetaOptimisticallyContext;
    },

    onError: (_err, _data, context) => {
      if ((context as TDeleteIntegrationMetaOptimisticallyContext)?.previousData !== undefined) {
        queryClient.setQueryData(
          [GET_LIST_INTEGRATION_META_QUERY_KEY],
          (context as TDeleteIntegrationMetaOptimisticallyContext).previousData
        );
      }
    },

    ...restConfig,
  });
};
